'use client';

import { useMemo } from 'react';
import { useLocale } from '@/components/LocaleProvider';
import { localeIntlTags } from '@/i18n/config';

type DateInput = string | number | Date;

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

/**
 * Locale-aware date formatting tied to the active UI locale (from LocaleProvider),
 * NOT the browser locale. Replaces `new Date(x).toLocaleDateString()` etc. so a
 * user who picks Portuguese sees pt-PT dates regardless of their browser.
 *
 * `date()` with no options mirrors the old `toLocaleDateString()` default
 * (numeric short date) — just resolved against the app locale.
 *
 *   const df = useDateFormatter();
 *   df.date(task.dueDate)                 // 05/01/2026 (en-GB) · 05/01/2026 (pt-PT)
 *   df.dateTime(scan.createdAt)           // 05/01/2026, 14:30
 *   df.date(x, { dateStyle: 'medium' })   // 5 Jan 2026
 */
export function useDateFormatter() {
  const { locale } = useLocale();
  const tag = localeIntlTags[locale];

  return useMemo(() => {
    return {
      /** Numeric short date by default (mirrors toLocaleDateString). */
      date: (value: DateInput, options?: Intl.DateTimeFormatOptions) =>
        new Intl.DateTimeFormat(tag, options).format(toDate(value)),
      /** Short date + short time (mirrors toLocaleString for dates). */
      dateTime: (value: DateInput, options?: Intl.DateTimeFormatOptions) =>
        new Intl.DateTimeFormat(tag, {
          dateStyle: 'short',
          timeStyle: 'short',
          ...options,
        }).format(toDate(value)),
      /** Short time (mirrors toLocaleTimeString). */
      time: (value: DateInput, options?: Intl.DateTimeFormatOptions) =>
        new Intl.DateTimeFormat(tag, { timeStyle: 'short', ...options }).format(
          toDate(value),
        ),
      /** The resolved BCP-47 tag, for callers that need a raw Intl locale. */
      tag,
    };
  }, [tag]);
}
